import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IOptionGroup, IOptionGroupArticle, IOptionGroupOption } from "@/data/model/OptionGroup";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { PortalFormat } from "@/portal/util/PortalFormat";

export class OptionGroupTableController {
    private $element: JQuery;
    private dao: CatalogDao;
    private dataTable: DataTable<IOptionGroup>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        const tableConfig: IDataTableConfig<IOptionGroup> = {
            columnMapping: [
                {
                    name: i18n("general.fields.designation"),
                    property: "internalName",
                    styles: { width: "20%" },
                    formatter: (m) => {
                        if (m.internalName !== undefined) return Format.htmlEscape(m.internalName);

                        return Format.htmlEscape(translate(m.name));
                    },
                },
                {
                    name: i18n("general.entities.options"),
                    property: "options",
                    styles: { width: "31%" },
                    formatter: (m, p) => {
                        return PortalFormat.toCommaSeparatedList<IOptionGroupOption>(
                            p,
                            (c) =>
                                Format.htmlEscape(
                                    c.article.internalName !== undefined
                                        ? c.article.internalName
                                        : translate(c.article.name)
                                )!,
                            (c) => "/article/" + c.article.id,
                            4
                        );
                    },
                },
                {
                    name: i18n("optionGroup.fields.appearsInItems"),
                    property: "articles",
                    styles: { width: "28%" },
                    formatter: (m, p) => {
                        return PortalFormat.toCommaSeparatedList<IOptionGroupArticle>(
                            p,
                            (c) =>
                                Format.htmlEscape(
                                    c.article.internalName !== undefined
                                        ? c.article.internalName
                                        : translate(c.article.name)
                                )!,
                            (c) => "/article/" + c.article.id,
                            4
                        );
                    },
                },
                {
                    name: i18n("general.fields.internalNote"),
                    property: "internalNote",
                    styles: { width: "16%" },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: true,
            locationDependend: true,
            defaultSortProperty: "internalName",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.dataTable = new DataTable<IOptionGroup>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.optionGroupDataSource,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/optiongroup/" + m.id;
            },
            (m) => {
                window.open("/optiongroup/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }
}
